import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '../theme';

interface SectionModalProps {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  imageCaption?: string;
  sections: { [key: string]: string[] | string };
  color: string;
  isOpen: boolean;
  onClose: () => void;
  modalWidth?: number | null;
  defaultExpandedSections?: string[]; // Sections à ouvrir par défaut
}

// Fonction pour parser le markdown simple
const parseMarkdown = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>');
};

const SectionModal: React.FC<SectionModalProps> = ({
  title,
  subtitle,
  imageUrl,
  imageCaption,
  sections,
  color,
  isOpen,
  onClose,
  modalWidth: propModalWidth,
  defaultExpandedSections = []
}) => {
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [modalWidth, setModalWidth] = useState<number | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Créer une clé stable basée sur les clés de sections pour éviter les re-renders inutiles
  const sectionsKey = useMemo(() => Object.keys(sections).sort().join(','), [sections]);
  const defaultExpandedKey = useMemo(() => defaultExpandedSections.sort().join(','), [defaultExpandedSections]);

  // Initialiser les sections : fermées par défaut, sauf celles spécifiées dans defaultExpandedSections
  useEffect(() => {
    if (isOpen) {
      const initialExpanded: { [key: string]: boolean } = {};
      Object.keys(sections).forEach(key => {
        initialExpanded[key] = defaultExpandedSections.includes(key);
      });
      setExpandedSections(initialExpanded);
    }
  }, [isOpen, sectionsKey, defaultExpandedKey]);

  // Réinitialiser l'erreur d'image quand l'image change ou que la modale s'ouvre
  useEffect(() => {
    if (isOpen) {
      setImageError(false);
      setIsClosing(false);
      // Nettoyer le timeout si la modale est rouverte pendant la fermeture
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    }
  }, [isOpen, imageUrl]);

  // Nettoyer le timeout au démontage
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Gérer la fermeture avec animation
  const handleClose = useCallback(() => {
    if (isClosing || !isOpen) return; // Éviter les doubles fermetures
    setIsClosing(true);
    // Nettoyer le timeout précédent s'il existe
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsClosing(false);
      onClose();
      closeTimeoutRef.current = null;
    }, 300); // Durée de l'animation de fermeture
  }, [isClosing, isOpen, onClose]);

  // Calculer la largeur du modal basée sur l'image
  useEffect(() => {
    if (propModalWidth !== undefined) {
      setModalWidth(propModalWidth);
    } else if (isOpen && imageUrl && !imageError) {
      const img = new Image();
      img.onload = () => {
        const maxHeight = 500;
        const aspectRatio = img.width / img.height;
        const calculatedWidth = Math.min(img.width, maxHeight * aspectRatio);
        setModalWidth(calculatedWidth + 40);
      };
      img.onerror = () => {
        setImageError(true);
        setModalWidth(500);
      };
      img.src = imageUrl;
    } else if (isOpen && (!imageUrl || imageError)) {
      // Si pas d'image ou erreur, utiliser une largeur par défaut
      setModalWidth(500);
    }
  }, [isOpen, imageUrl, propModalWidth, imageError]);

  const toggleSection = useCallback((sectionTitle: string) => {
    setExpandedSections(prev => {
      const newState = {
        ...prev,
        [sectionTitle]: !prev[sectionTitle]
      };
      
      // Si on ouvre une section, faire remonter le contenu avec scrollIntoView
      if (!prev[sectionTitle]) {
        setTimeout(() => {
          const modalElement = document.querySelector('.modal-scroll');
          if (modalElement) {
            // Trouver l'élément de la section qui s'ouvre
            const sectionElement = modalElement.querySelector(`[data-section="${sectionTitle}"]`);
            if (sectionElement) {
              // Scroll pour que la section soit visible en haut
              sectionElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            } else {
              // Fallback: scroll en haut du modal
              modalElement.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }
          }
        }, 200); // Délai pour laisser l'animation se terminer
      }
      
      return newState;
    });
  }, []);

  // Ne pas rendre si la modale n'est pas ouverte et n'est pas en train de se fermer
  if (!isOpen && !isClosing) return null;

  return createPortal(
    <React.Fragment>
      {/* Styles pour l'animation du modal */}
      <style>
        {`
          @keyframes backdropFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes backdropFadeOut {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
            }
          }
          
          @keyframes modalSlideUp {
            0% {
              opacity: 0;
              transform: translateY(40px) scale(0.95);
            }
            60% {
              transform: translateY(-5px) scale(1.02);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes modalSlideDown {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            100% {
              opacity: 0;
              transform: translateY(40px) scale(0.95);
            }
          }
        `}
      </style>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '20px',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: isClosing ? 'backdropFadeOut 0.3s ease-out forwards' : 'backdropFadeIn 0.2s ease-out',
          pointerEvents: isClosing ? 'none' : 'auto'
        }}
        onClick={handleClose}
      >
        <div 
          className="modal-scroll"
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: modalWidth ? `${modalWidth}px` : 'fit-content',
            minWidth: '200px',
            maxWidth: '90vw',
            height: 'auto',
            maxHeight: '85vh',
            overflow: 'auto',
            padding: '0',
            position: 'relative',
            border: `4px solid ${color}`,
            scrollbarWidth: 'thin',
            scrollbarColor: `${color} #f1f1f1`,
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            animation: isClosing ? 'modalSlideDown 0.3s ease-in forwards' : 'modalSlideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            display: 'flex',
            flexDirection: 'column',
            willChange: 'transform, opacity',
            pointerEvents: isClosing ? 'none' : 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* En-tête avec fond coloré et texte blanc */}
          <div style={{
            background: color,
            padding: '15px 60px 15px 15px',
            margin: 0,
            borderRadius: 0,
            color: 'white',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            width: 'fit-content',
            minWidth: '100%',
            position: 'relative',
            boxSizing: 'border-box'
          }}>
            <h2 style={{ 
              margin: 0, 
              color: 'white',
              fontSize: '28px',
              fontWeight: '700',
              fontFamily: theme.fonts.title,
              paddingRight: '0',
              wordBreak: 'break-word'
            }}>
              {title}
            </h2>
            {subtitle && (
              <p style={{
                margin: '5px 0 0 0',
                color: 'white',
                fontSize: '16px',
                fontWeight: '500',
                fontFamily: theme.fonts.title,
                opacity: 0.9,
                wordBreak: 'break-word'
              }}>
                {subtitle}
              </p>
            )}
            <button 
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                fontSize: '36px',
                cursor: 'pointer',
                color: 'white',
                width: 'auto',
                height: 'auto',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                zIndex: 10,
                padding: '4px 8px',
                lineHeight: '1',
                fontWeight: '300',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              ×
            </button>
          </div>

          {/* Photo centrée */}
          {imageUrl && !imageError && (
            <div style={{
              padding: '10px 0',
              margin: '0',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <img 
                src={imageUrl} 
                alt={title}
                onError={() => setImageError(true)}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
              {imageCaption && (
                <div style={{
                  padding: '10px 15px',
                  fontSize: '13px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  backgroundColor: 'transparent',
                  border: 'none',
                  margin: '0',
                  maxWidth: '100%',
                  wordWrap: 'break-word'
                }}>
                  {imageCaption}
                </div>
              )}
            </div>
          )}

          {/* Sections déroulantes */}
          <div style={{
            padding: '10px',
            background: 'white',
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            {Object.entries(sections).map(([sectionTitle, items], index) => (
              <div 
                key={sectionTitle} 
                data-section={sectionTitle}
                style={{
                  border: 'none',
                  borderRadius: 0,
                  marginBottom: 0,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  borderBottom: `1px solid ${color}`,
                  borderTop: index === 0 ? `1px solid ${color}` : 'none',
                  WebkitTransform: 'translateZ(0)', // Force GPU rendering pour meilleur rendu sur iOS
                  transform: 'translateZ(0)'
                }}
              >
                <button 
                  onClick={() => toggleSection(sectionTitle)}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    background: expandedSections[sectionTitle] ? '#f8f9fa' : 'white',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s ease',
                    fontFamily: theme.fonts.title,
                    fontSize: '16px',
                    fontWeight: 600,
                    color: color
                  }}
                >
                  {sectionTitle}
                  <span style={{
                    transition: 'transform 0.3s ease',
                    fontSize: '16px',
                    color: color,
                    fontWeight: 'bold',
                    display: 'inline-block',
                    width: '20px',
                    height: '20px',
                    textAlign: 'center',
                    lineHeight: '20px',
                    transform: 'scale(1.2)',
                    transformOrigin: 'center'
                  }}>
                    {expandedSections[sectionTitle] ? '⌃' : '⌄'}
                  </span>
                </button>
                <div style={{
                  maxHeight: expandedSections[sectionTitle] ? '500px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                  background: 'white'
                }}>
                  <div style={{
                    padding: '10px 15px',
                    lineHeight: 1.6,
                    color: '#555',
                    fontSize: '14px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {Array.isArray(items) ? (
                      <ul style={{ 
                        margin: 0, 
                        paddingLeft: '20px',
                        listStyleType: 'disc',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ 
                            marginBottom: '8px',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}
                          dangerouslySetInnerHTML={{ __html: parseMarkdown(item) }}
                          />
                        ))}
                      </ul>
                    ) : (
                      // Si le texte contient des sauts de ligne doubles, créer une liste avec bullet points
                      (typeof items === 'string' && items.includes('\n\n')) ? (
                        <ul style={{ 
                          margin: 0, 
                          paddingLeft: '20px',
                          listStyleType: 'disc',
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {items.split('\n\n').filter(line => line.trim()).map((line, lineIndex) => (
                            <li key={lineIndex} style={{ 
                              marginBottom: '8px',
                              width: '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}
                            dangerouslySetInnerHTML={{ __html: parseMarkdown(line.trim()) }}
                            />
                          ))}
                        </ul>
                      ) : (
                        <p style={{ 
                          margin: 0,
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(items) }}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </React.Fragment>,
    document.body
  );
};

export default SectionModal;

